import React from "react";
import styled, { withTheme, css } from "styled-components";
import ProgressBar from "react-progressbar";
import AudioVersionDropdown from "./AudioVersionDropdown";
import { withBible, BibleCtx } from "../../Bible/BibleContainer";
import { withChapter, ChapterCtx } from "../ChapterContainer";
import { withAudio, AudioCtx } from "./ChapterAudio";
import ForwardIcon from "icons/Timer10ForwardIcon";
import ReplayIcon from "icons/Timer10Icon";
import PlayIcon from "icons/PlayButtonIcon";
import PauseIcon from "icons/PauseButtonIcon";
import media from "utils/media";
import { AudioBible } from "api/typedefs/Bible";
import { withRouter, RouteComponentProps } from "react-router-dom";

const PlayerDiv = styled.div`
  padding: 0px;
  margin: 0px;
  width: 100%;
  height: 84px;
  background: ${props =>
    props.theme.darkMode ? props.theme.computedPrimary : "white"};
  box-shadow: 1px -3px 3px rgba(0, 0, 0, 0.05);
  ${media.desktopEmbedSafe`
    height: auto;
  `}

  font-family: 'Noto Sans', sans-serif;
  direction: ${props => props.theme.scriptDirection || "ltr"};
  border-radius: 2px;
  text-align: center;
  h3,
  h4,
  p {
    font-size: 20px;
    margin: 0;
    padding: 0;
  }
  h3 {
    margin-bottom: 0.3em;
    font-weight: 700;
  }

  h4 {
    font-size: 12px;
    color: #aaa;
    margin-bottom: 0.3em;
    font-weight: 700;
  }
  a {
    color: ${props => props.theme.primaryText};
    display: block;
    padding: 16px;
    text-decoration: none;
    transition: background-color 0.6s ease;
    &:hover {
      background-color: ${props => props.theme.hightlightColor};
    }
  }
`;

const AudioHeader = styled.div`
  display: none;
`;

const AudioHeaderMobile = styled.div`
  font-size: 0.9em;
  font-weight: 700;
  display: flex;
  flex: 0 0 100%;
  flex-direction: row;
  padding: 0px;
  margin: auto;
  margin-top: -20px;
  max-width: calc(100vw - 80px);
  svg {
    vertical-align: baseline !important;
  }
  div {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  ${media.desktopEmbedSafe`
    margin: 0px;
    margin-bottom: 0px;
    margin-top: 0px;
    padding: 0px 16px;
  `}
`;

const ControlsFlexCol = styled.div`
  display: flex;
  flex: 0 0 100%;
  padding: 0px;
  margin: 0px;
  flex-direction: column-reverse;
`;

const StyledPauseIcon = styled(PauseIcon)`
  padding: 5px;
`;

const StyledPlayIcon = styled(PlayIcon)`
  padding: 5px;
`;

const LoadingSvg = styled.svg`
  animation: rotate 2s linear infinite;
  height: 56px;
  width: 56px;
`;

const LoadingCircle = styled.circle`
  stroke-dasharray: 1, 200;
  stroke-dashoffset: 0;
  animation: dash 1.5s ease-in-out infinite;
  stroke-linecap: round;
  /* site primary color here   */
  stroke: ${props => props.theme.computedPrimary};
  stroke-width: 2;
  stroke-miterlimit: 10;
`;

const LoadingBackground = styled.circle`
  stroke-linecap: round;
  stroke: white;
  stroke-width: 4;
`;

const LoadingAnimation = styled.div`
  position: absolute;

  @keyframes rotate {
    100% {
      transform: rotate(360deg);
    }
  }

  @keyframes dash {
    0% {
      stroke-dasharray: 1, 200;
      stroke-dashoffset: 0;
    }
    50% {
      stroke-dasharray: 89, 200;
      stroke-dashoffset: -35;
    }
    100% {
      stroke-dasharray: 89, 200;
      stroke-dashoffset: -124;
    }
  }

  @keyframes color {
    100%,
    0% {
      stroke: #d62d20;
    }
    40% {
      stroke: #0057e7;
    }
    66% {
      stroke: #008744;
    }
    80%,
    90% {
      stroke: #ffa700;
    }
  }
`;

const PlayControl = styled.div<{ isPlayable: boolean }>`
  padding-right: 4px;
  padding-left: 4px;
  padding-top: 8px;
  z-index: 1;

  svg {
    ${props => {
      if (!props.isPlayable) {
        return css`
          color: #a2a2a2;
        `;
      }
      return css`
        color: ${props.theme.computedPrimary};
      `;
    }}
`;

const PrevNextControl = styled.div`
  svg {
    transition: all 0.5s ease;
    color: #a2a2a2;
  }
  z-index: 1;

  &:hover {
    svg {
      color: ${props => props.theme.primaryText};
    }
  }
`;

const ControlBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 100%;
  margin-top: -10px;
  ${media.desktopEmbedSafe`
    margin-top: -45px;
  `}
`;

const SeekBar = styled.div`
  display: flex;
  flex: 0 0 100%;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-top: 0px;
`;

const TimeBar = styled.div`
  display: flex;
  flex: 0 0 100%;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 0px 16px;
  pointer-events: none;
`;

const StyledProgressBar = styled(ProgressBar)`
  background-color: ${({ theme }) => (theme.darkMode ? "#666" : "#f2f2f2")};
  width: 100%;
  padding: 0px;
  margin-bottom: 5px;
`;

const TimeDisplayMobile = styled.div`
  font-size: 12px;
  color: ${({ theme }) => (theme.darkMode ? "white" : "#aaa")};
  font-weight: 700;
`;

const TimeDisplay = styled.div`
  display: none;
`;

interface AudioControllerProps
  extends RouteComponentProps<{ bibleId: string }> {
  chapter: ChapterCtx;
  bible: BibleCtx;
  audio: AudioCtx;
  theme: any;
  setModeAudioSync: () => void;
  setModeDefault: () => void;
}

class AudioController extends React.Component<AudioControllerProps> {
  shouldComponentUpdate = (nextProps: AudioControllerProps) =>
    nextProps.chapter.getChapter() !== undefined;

  padNumber = (value: number | string, length: number) => {
    let result = `${value}`;
    while (result.length < length) {
      result = `0${result}`;
    }
    return result;
  };

  secondsToTimeString = (rawSeconds: number) => {
    const seconds = Math.floor(rawSeconds) % 60;
    let minutes = Math.floor(rawSeconds / 60);
    let hours = Math.floor(minutes / 60);
    minutes %= 60;
    const days = Math.floor(hours / 24);
    hours %= 24;
    let result = "";
    if (days > 0) {
      result += `${days} days `;
    }

    if (hours > 0) {
      result += `${hours}:`;
    }

    return `${result}${minutes}:${this.padNumber(seconds, 2)}`;
  };

  handleForward = () => {
    if (this.props.audio.isPlayable && !this.props.audio.isLoading) {
      this.props.audio.onAudioSeek(this.props.audio.position + 10);
    }
  };

  handleReplay = () => {
    if (this.props.audio.isPlayable && !this.props.audio.isLoading) {
      this.props.audio.onAudioSeek(this.props.audio.position - 10);
    }
  };

  handlePlayPause = () => {
    if (this.props.audio.isPlayable) {
      if (
        this.props.audio.getCurrentAudioChapter().timecodes &&
        !this.props.audio.isPlaying
      ) {
        this.props.setModeAudioSync();
      } else {
        this.props.setModeDefault();
      }
      this.props.audio.onPlayPause();
    }
  };

  renderAudioVersions = (audioBibles: AudioBible[]) => {
    const { selectedAudioBibleId, onSelectAudioBible } = this.props.audio;
    return (
      <AudioVersionDropdown
        audioBibles={audioBibles}
        selectedAudioBibleId={selectedAudioBibleId}
        onSelectAudioBible={onSelectAudioBible}
      />
    );
  };

  render = () => {
    const { theme, audio } = this.props;
    let progress = 0;
    if (audio.duration !== 0) {
      progress = (audio.position / audio.duration) * 100;
    }

    const { bibleId } = this.props.match.params;
    const currentBible = this.props.bible.biblesById[bibleId];
    let audioBibles: AudioBible[] | undefined;
    if (
      currentBible &&
      currentBible.audioBibles &&
      currentBible.audioBibles.length > 0
    ) {
      audioBibles = currentBible.audioBibles;
    }

    const currentChapter = this.props.chapter.getChapter();
    const pausePlayColor = theme.darkMode ? theme.textOnPrimary : theme.primary;
    const progressBarColor = theme.light
      ? theme.selectedColor
      : theme.darkMode
      ? "#ccc"
      : theme.primary;

    if (currentChapter && audioBibles) {
      return (
        <PlayerDiv>
          <AudioHeader>
            <h3>{currentChapter.reference}</h3>
            {this.renderAudioVersions(audioBibles)}
          </AudioHeader>
          <ControlsFlexCol>
            <ControlBar>
              <PrevNextControl>
                <ReplayIcon size={24} onClick={this.handleReplay} />
              </PrevNextControl>
              <PlayControl
                onClick={this.handlePlayPause}
                isPlayable={audio.isPlayable}
              >
                {audio.isPlayPending && (
                  <LoadingAnimation>
                    <LoadingSvg>
                      <LoadingBackground cx="28" cy="28" r="21.5" fill="none" />
                      <LoadingCircle cx="28" cy="28" r="21.5" fill="none" />
                    </LoadingSvg>
                  </LoadingAnimation>
                )}
                {audio.isPlaying ? (
                  <StyledPauseIcon size={46} color={pausePlayColor} />
                ) : (
                  <StyledPlayIcon size={46} color={pausePlayColor} />
                )}
              </PlayControl>
              <PrevNextControl>
                <ForwardIcon size={24} onClick={this.handleForward} />
              </PrevNextControl>
            </ControlBar>
            <AudioHeaderMobile>
              {this.renderAudioVersions(audioBibles)}
            </AudioHeaderMobile>
            <TimeBar>
              <TimeDisplayMobile>
                {this.secondsToTimeString(audio.position)}
              </TimeDisplayMobile>
              <TimeDisplayMobile>
                {this.secondsToTimeString(audio.duration)}
              </TimeDisplayMobile>
            </TimeBar>
            <SeekBar>
              <TimeDisplay>
                {this.secondsToTimeString(audio.position)}
              </TimeDisplay>
              <StyledProgressBar
                completed={progress}
                height={6}
                color={progressBarColor}
              />
              <TimeDisplay>
                {this.secondsToTimeString(audio.duration)}
              </TimeDisplay>
            </SeekBar>
          </ControlsFlexCol>
        </PlayerDiv>
      );
    }
    return null;
  };
}

export default withRouter(
  withChapter(withAudio(withBible(withTheme(AudioController))))
);
